import React, { useState, useEffect, useRef } from 'react';
import api from '../lib/api';
import { Wallet as WalletType, Transaction } from '../types/api';

const Wallet = () => {
  const [wallet, setWallet] = useState<WalletType | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [amount, setAmount] = useState(0);
  const [success, setSuccess] = useState('');
  const [showCustomAmount, setShowCustomAmount] = useState(false);
  const customAmountInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [walletRes, txRes] = await Promise.all([
          api.get('/wallet/me'),
          api.get('/wallet/transactions')
        ]);
        setWallet(walletRes.data);
        setTransactions(txRes.data);
      } catch (error) {
        console.error(error);
      }
    };

    fetchData();
  }, []);

  const handleTopUp = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const response = await api.post('/wallet/topup', { amount });
      setWallet(response.data);

      // Refresh transactions
      const txRes = await api.get('/wallet/transactions');
      setTransactions(txRes.data);

      setSuccess(`Úspěšně přidáno ${amount} kreditů!`);
      setAmount(0);
      setShowCustomAmount(false);
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error(error);
    }
  };

  const quickTopUp = async (value: number) => {
    try {
      const response = await api.post('/wallet/topup', { amount: value });
      setWallet(response.data);

      // Refresh transactions
      const txRes = await api.get('/wallet/transactions');
      setTransactions(txRes.data);

      setSuccess(`Úspěšně přidáno ${value} kreditů!`);
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-secondary-50 to-accent-50 p-4 sm:p-6 lg:p-8">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-8 sm:mb-12">
          <h2 className="text-4xl sm:text-5xl font-bold bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent mb-2">
            Vaše peněženka
          </h2>
          <p className="text-gray-600">Spravujte kredity a doplňujte zůstatek</p>
        </div>

        {success && (
          <div className="mb-6 p-4 text-center text-green-700 bg-green-50 rounded-2xl border border-green-200">
            {success}
          </div>
        )}

        {wallet && (
          <div className="mb-6 sm:mb-8">
            <div className="bg-gradient-to-br from-primary-500 via-secondary-500 to-accent-500 p-8 sm:p-10 rounded-3xl shadow-card-hover transform transition-all duration-200">
              <div className="text-white">
                <p className="text-lg sm:text-xl mb-2 opacity-90">Dostupný zůstatek</p>
                <p className="text-5xl sm:text-6xl md:text-7xl font-bold mb-4">
                  {wallet.balance}
                  <span className="text-2xl sm:text-3xl ml-2">kreditů</span>
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-6 sm:space-y-8">
          <div className="bg-white/80 backdrop-blur-lg p-6 sm:p-8 rounded-3xl shadow-card border border-white">
            <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 flex items-center">
              Rychlé dobití
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
              <button
                onClick={() => quickTopUp(10)}
                className="bg-gradient-to-r from-primary-500 to-primary-600 text-white p-4 sm:p-5 rounded-2xl font-bold text-lg sm:text-xl hover:shadow-glow transform transition-all duration-200"
              >
                +10
                <div className="text-xs sm:text-sm mt-1 opacity-80">kreditů</div>
              </button>
              <button
                onClick={() => quickTopUp(25)}
                className="bg-gradient-to-r from-secondary-500 to-secondary-600 text-white p-4 sm:p-5 rounded-2xl font-bold text-lg sm:text-xl hover:shadow-glow transform transition-all duration-200"
              >
                +25
                <div className="text-xs sm:text-sm mt-1 opacity-80">kreditů</div>
              </button>
              <button
                onClick={() => quickTopUp(50)}
                className="bg-gradient-to-r from-accent-500 to-accent-600 text-white p-4 sm:p-5 rounded-2xl font-bold text-lg sm:text-xl hover:shadow-glow transform transition-all duration-200"
              >
                +50
                <div className="text-xs sm:text-sm mt-1 opacity-80">kreditů</div>
              </button>
              <button
                onClick={() => quickTopUp(100)}
                className="bg-gradient-to-r from-primary-600 to-accent-600 text-white p-4 sm:p-5 rounded-2xl font-bold text-lg sm:text-xl hover:shadow-glow transform transition-all duration-200"
              >
                +100
                <div className="text-xs sm:text-sm mt-1 opacity-80">kreditů</div>
              </button>
              <button
                onClick={() => {
                  setShowCustomAmount(true);
                  setTimeout(() => customAmountInputRef.current?.focus(), 150);
                }}
                className="bg-white border-2 border-dashed border-gray-300 text-gray-800 p-4 sm:p-5 rounded-2xl font-bold text-lg sm:text-xl hover:border-primary-400 hover:text-primary-700 transition-all duration-200"
              >
                Jiná
                <div className="text-xs sm:text-sm mt-1 opacity-80">zadat vlastní částku</div>
              </button>
            </div>

            {showCustomAmount && (
              <form onSubmit={handleTopUp} className="space-y-4 sm:space-y-6 mt-6 border-t border-gray-100 pt-6">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-3">
                    Zadejte částku (kredity)
                  </label>
                  <input
                    ref={customAmountInputRef}
                    type="number"
                    min="1"
                    required
                    className="w-full p-4 text-gray-900 text-lg sm:text-xl font-bold bg-white rounded-2xl border-2 border-gray-200 focus:outline-none focus:border-primary-500 focus:ring-4 focus:ring-primary-100 transition-all duration-200"
                    placeholder="0"
                    value={amount || ''}
                    onChange={(e) => setAmount(Number(e.target.value))}
                  />
                </div>
                <div className="flex flex-col sm:flex-row gap-3">
                  <button
                    type="submit"
                    className="w-full sm:w-auto flex-1 py-4 sm:py-5 font-bold text-white text-lg sm:text-xl bg-gradient-to-r from-primary-500 to-secondary-500 rounded-2xl shadow-lg hover:shadow-glow transform transition-all duration-200"
                  >
                    Dobít kredity
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowCustomAmount(false);
                      setAmount(0);
                    }}
                    className="w-full sm:w-auto px-4 py-4 font-bold text-gray-700 bg-gray-100 rounded-2xl border border-gray-200 hover:bg-gray-200 transition-all duration-200"
                  >
                    Zavřít
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>

        <div className="mt-6 sm:mt-8 bg-white/60 backdrop-blur-sm p-6 rounded-2xl border border-white/50">
          <h4 className="font-bold text-gray-900 mb-3 text-lg">Jak fungují kredity</h4>
          <ul className="space-y-2 text-gray-700 text-sm sm:text-base">
            <li className="flex items-start">
              <span className="mr-2">•</span>
              <span>Platí se podle aktuální ceny herního místa, částka se odečte po ukončení hry.</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">•</span>
              <span>Vždy udržujte dostatečný zůstatek, aby šla hra spustit plynule.</span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">•</span>
              <span>Historii dobití a plateb najdete níže v přehledu transakcí.</span>
            </li>
          </ul>
        </div>
      </div>

      <div className="mt-12 bg-white/80 backdrop-blur-lg p-8 rounded-3xl shadow-card border border-white">
        <h3 className="text-2xl font-bold text-gray-900 mb-6">Historie transakcí</h3>
        {transactions.length === 0 ? (
          <div className="text-center text-gray-600 py-8">
            Zatím žádné transakce.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left border-b border-gray-200">
                  <th className="pb-4 font-bold text-gray-600">Datum</th>
                  <th className="pb-4 font-bold text-gray-600">Typ</th>
                  <th className="pb-4 font-bold text-gray-600 text-right">Částka</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {transactions.map((tx: Transaction) => (
                  <tr key={tx.id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="py-4 text-gray-900">
                      {new Date(tx.createdAt).toLocaleDateString()} {new Date(tx.createdAt).toLocaleTimeString()}
                    </td>
                    <td className="py-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-bold ${tx.type === 'TOPUP' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                        }`}>
                        {tx.type === 'TOPUP' ? 'Dobití' : 'Platba'}
                      </span>
                    </td>
                    <td className={`py-4 text-right font-bold ${tx.type === 'TOPUP' ? 'text-green-600' : 'text-red-600'
                      }`}>
                      {tx.type === 'TOPUP' ? '+' : '-'}{tx.amount}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Wallet;
